import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useIntl } from "react-intl";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { UserData } from "checkout/ts/redux/models/user-data";
import { parsePhone } from "checkout/ts/redux/utils/intl";
import { isTransactionThreeDsAddressRequired } from "checkout/ts/redux/utils/token";
import { isDarkTheme } from "checkout/ts/utils/colors";
import { UnivaMetadata } from "checkout/ts/utils/metadata";
import { getStepUrl, StepName } from "checkout/ts/utils/StepName";
import { isEmpty } from "lodash";
import { compose } from "recompose";
import { Form, formValueSelector, InjectedFormProps, reduxForm } from "redux-form";
import { PaymentType, TransactionTokenCardDataItem } from "univapay-node";

import { FORM_CUSTOMER_INFO, FORM_TOKEN_SELECT_NAME } from "../../../../../common/constants";
import { LOCALE_LABELS } from "../../../locale/labels";
import { Dispatch, StateShape } from "../../../redux/store";
import { validationToErrors } from "../../../utils/errors";
import { Regexes } from "../../../utils/Regexes";
import { StepTitle } from "../../common/StepTitle";
import { PhoneNumberField } from "../../forms/PhoneNumberInput";
import { TextField } from "../../forms/Text";
import { DialogButton } from "../../layout/DialogButton";
import { validatePhoneNumber } from "../card/utils/phoneNumber";
import { validateEmail } from "../card/utils/validateEmail";

import { CustomFields } from "./components/CustomFields";
import { getRequiredFields } from "./utils/customerInfo";
import { isValidDate } from "./utils/date";
import { AcceptField, PrivacyLink } from "./FormData";

type OwnProps = {
    title?: string;
    onSubmit?: (values: Partial<UserData>) => void | Promise<void>;
    nextButtonLabel: React.ReactNode;
    showPrivacyLink?: boolean;
    showAcceptField?: boolean;
};

type ComposedProps = ReturnType<typeof stateSelector> & InjectedFormProps<Partial<UserData>> & OwnProps;

const stateSelector = (state: StateShape) => {
    const { checkout, application, userData, configuration, tokens } = state;

    const { invalidFields } = application;

    const {
        email,
        phoneNumber,
        customerName,
        customerNameKana,
        submitButtonText,
        requireEmail: appParamsRequireEmail,
        requirePhoneNumber: appParamsRequirePhoneNumber,
        customFields,
        instantChargeTokenId,
        merchantCardRegistration,
    } = application.params.params;

    const selectedTokenId = formValueSelector(FORM_TOKEN_SELECT_NAME)(state, "selectedTokenId");
    const customerIdToken = tokens.tokens?.[selectedTokenId];

    const threeDsAddressRequired = isTransactionThreeDsAddressRequired(
        state.application.params?.params,
        state.configuration?.data
    );

    const {
        requireEmail,
        requirePhoneNumber,
        requireBillingAddress,
        requireName,
        requireNameKana,
        forceThreeDsPhoneNumber,
    } = getRequiredFields(checkout.paymentType, application, configuration?.data, customerIdToken);

    const isKonbini = checkout.paymentType === PaymentType.KONBINI;

    return {
        paymentType: checkout.paymentType,
        paymentMethodKey: checkout.paymentMethodKey,
        submitButtonText,
        showName: checkout.paymentType === PaymentType.ONLINE,
        requireEmail: !customerIdToken && (isKonbini ? requireEmail || appParamsRequireEmail !== false : requireEmail),
        requirePhoneNumber: isKonbini
            ? requirePhoneNumber || appParamsRequirePhoneNumber !== false || !userData.phoneNumber?.localNumber
            : requirePhoneNumber,
        requireBillingAddress,
        processing: ((state.loading.models.userData || state.loading.models.checkout) as unknown) as boolean,
        requireName: !customerIdToken && requireName,
        requireNameKana: !customerIdToken && requireNameKana,
        initialValues: {
            email,
            phoneNumber:
                parsePhone(phoneNumber) ||
                (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.phoneNumber ||
                parsePhone(customerIdToken?.metadata?.[UnivaMetadata.PHONE_NUMBER]),
            name: customerName,
            nameKana: customerNameKana,
        },
        customerIdToken,
        darkTheme: isDarkTheme(state),
        customFields,
        instantChargeTokenId,
        forceThreeDsPhoneNumber,
        threeDsAddressRequired,
        merchantCardRegistration,
        invalidFields,
        userData,
    };
};

export const validate = (
    values: Partial<UserData>,
    {
        requireEmail,
        requirePhoneNumber,
        requireName,
        customFields,
        customerIdToken,
        forceThreeDsPhoneNumber,
        showAcceptField,
        paymentType,
    }: ComposedProps
) => {
    const { email, name, phoneNumber, nameKana, birthday, accept } = values;

    const validationToErrorsUserData = {
        "email": validateEmail(email, !customerIdToken && requireEmail),
        "phoneNumber": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]:
                (requirePhoneNumber || forceThreeDsPhoneNumber) &&
                (!phoneNumber?.localNumber || isEmpty(phoneNumber.localNumber)),
            [LOCALE_LABELS.VALIDATION_LOCAL_PHONE_NUMBER]: validatePhoneNumber(phoneNumber),
            [LOCALE_LABELS.VALIDATION_KONBINI_PHONE_NUMBER]:
                paymentType === PaymentType.KONBINI && phoneNumber?.countryCode !== "81",
        },
        "name": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: !customerIdToken && requireName && !name,
        },
        "nameKana": {
            [LOCALE_LABELS.VALIDATION_INVALID_CHARACTERS]:
                !customerIdToken && nameKana && !Regexes.Katakana.test(nameKana),
        },
        "birthday": {
            [LOCALE_LABELS.VALIDATION_DATE]: birthday && !isValidDate(birthday),
        },
        "accept": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: showAcceptField && !accept,
        },
    };

    const validationErrorsWithCustomFields = (() => {
        if (!customFields) {
            return validationToErrorsUserData;
        }

        return customFields.reduce((acc, { key, required }) => {
            acc[`customFields.${key}`] = {
                [LOCALE_LABELS.VALIDATION_REQUIRED]: required && !values.customFields?.[key],
            };
            return acc;
        }, validationToErrorsUserData);
    })();

    return validationToErrors(validationErrorsWithCustomFields);
};

export const Content = compose<ComposedProps, OwnProps>(
    connect(stateSelector),
    reduxForm({ destroyOnUnmount: false, forceUnregisterOnUnmount: true, form: FORM_CUSTOMER_INFO, validate })
)(
    ({
        paymentType,
        paymentMethodKey,
        processing,
        requireBillingAddress,
        requireEmail,
        requirePhoneNumber,
        requireName,
        requireNameKana,
        showName,
        submitButtonText,
        handleSubmit,
        title,
        onSubmit,
        darkTheme,
        nextButtonLabel,
        showPrivacyLink,
        showAcceptField,
        customFields,
        instantChargeTokenId,
        forceThreeDsPhoneNumber,
        threeDsAddressRequired,
        merchantCardRegistration,
        invalidFields,
        userData,
    }) => {
        const { push: redirect, replace: replaceUrl } = useHistory();
        const { formatMessage } = useIntl();

        const {
            userData: { setUserData },
            application: { clearInvalidFieldError },
        } = useDispatch<Dispatch>();

        const isPaidy = paymentType === PaymentType.PAIDY;

        const redirectToAddress =
            requireBillingAddress || paymentType === PaymentType.BANK_TRANSFER || threeDsAddressRequired;
        const stepName = instantChargeTokenId
            ? StepName.TOKEN_REVIEW
            : redirectToAddress
            ? StepName.ADDRESS
            : isPaidy
            ? StepName.PROCESSING
            : StepName.DATA;

        const handleFormSubmit = async (values: Partial<UserData>) => {
            setUserData(values);

            const submitCallback = onSubmit || (() => redirect(getStepUrl(paymentType, paymentMethodKey, stepName)));
            await submitCallback(values);
        };

        const showEmail = requireEmail || merchantCardRegistration;

        useEffect(() => {
            if (!paymentType) {
                return;
            }

            const shouldSkipCustomerInfo =
                !showName &&
                !requireName &&
                !showEmail &&
                !requirePhoneNumber &&
                !forceThreeDsPhoneNumber &&
                !requireNameKana &&
                !customFields?.length;

            if (shouldSkipCustomerInfo) {
                replaceUrl(getStepUrl(paymentType, paymentMethodKey, stepName));
            }
        }, []);

        return (
            <Form
                data-name="form-info"
                noValidate
                onSubmit={handleSubmit(handleFormSubmit)}
                className="info content-form">
                <Row>
                    <Col xs={12}>
                        <StepTitle>
                            {title || formatMessage({ id: LOCALE_LABELS.ONLINE_CUSTOMER_DETAIL_TITLE })}
                        </StepTitle>
                    </Col>

                    <Col xs={12}>
                        {(requireName || showName) && (
                            <TextField
                                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_NAME })}
                                bsSize="sm"
                                name="name"
                                autoComplete="name1"
                                tabIndex={3}
                                required={requireName}
                                isDark={darkTheme}
                            />
                        )}

                        {requireNameKana && (
                            <TextField
                                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_NAME_KANA })}
                                bsSize="sm"
                                name="nameKana"
                                autoComplete="name2"
                                tabIndex={3}
                                isDark={darkTheme}
                            />
                        )}

                        {showEmail && (
                            <TextField
                                label={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_EMAIL_LABEL })}
                                placeholder={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_EMAIL })}
                                tabIndex={3}
                                bsSize="sm"
                                name="email"
                                autoComplete="email"
                                type="email"
                                required
                                isDark={darkTheme}
                                fieldError={invalidFields.email?.error}
                                onChange={() => clearInvalidFieldError({ name: "email" })}
                            />
                        )}

                        {(requirePhoneNumber || forceThreeDsPhoneNumber) && (
                            <PhoneNumberField
                                label={formatMessage({ id: LOCALE_LABELS.FORM_FIELDS_PHONE_NUMBER_LABEL })}
                                tabIndex={3}
                                bsSize="sm"
                                name="phoneNumber"
                                autoComplete="phone"
                                type="phone"
                                required
                                isDark={darkTheme}
                                forcedCountryCode={isPaidy ? "81" : null}
                                fieldError={invalidFields.phoneNumber?.error}
                                currentPhoneNumber={userData?.phoneNumber}
                            />
                        )}

                        <CustomFields disabled={false} isDark={darkTheme} />
                    </Col>
                </Row>

                {showAcceptField && (
                    <Row>
                        <Col>
                            <AcceptField isDark={darkTheme} />
                        </Col>
                    </Row>
                )}

                {showPrivacyLink && <PrivacyLink isDark={darkTheme} />}

                <DialogButton disabled={processing} tabIndex={10}>
                    {nextButtonLabel || submitButtonText || (isPaidy && !requireBillingAddress)
                        ? formatMessage({ id: LOCALE_LABELS.COMMON_BUTTONS_SUBMIT })
                        : formatMessage({ id: LOCALE_LABELS.COMMON_BUTTONS_NEXT })}
                </DialogButton>
            </Form>
        );
    }
);

export default Content;
